import React, { useState } from 'react';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Show/hide card for the dashboard's graphs (2026-09-21 user request:
// "minimise other graphs, so I can see when I want to see"). Children are
// only mounted while open, so a collapsed chart costs nothing. Open/closed
// is remembered per card in localStorage (wrapped - it throws in private
// windows / when site data is blocked) and falls back to defaultOpen.
export const CollapsibleCard = ({ id, title, subtitle, defaultOpen = false, className = '', children }) => {
  const storageKey = `expenseDash.open.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOpen : saved === '1';
    } catch (e) {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch (e) { /* not persisted - fine */ }
      return next;
    });
  };
  return (
    <div className={`glass-card ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left"
      >
        <span>
          <span className="font-semibold text-gray-900">{title}</span>
          {subtitle && <span className="text-xs text-gray-400 ml-2">{subtitle}</span>}
        </span>
        <span className="text-xs text-gray-500 whitespace-nowrap">{open ? '▾ Hide' : '▸ Show'}</span>
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

// One spent-vs-budget bar (same idea as the cash-flow dashboard's
// BudgetBar). budget == null means "no budget set" - shown as plain spend,
// never as a 0% bar, so a project nobody budgeted doesn't read as "fine".
const BudgetBar = ({ label, spent, budget, hint }) => {
  if (budget == null) {
    return (
      <div className="grid grid-cols-[80px_1fr] items-center gap-2 text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-400" title={hint}>No budget set - spent {money(spent)}</span>
      </div>
    );
  }
  const pct = budget > 0 ? spent / budget : 0;
  const color = pct > 1 ? 'bg-red-500' : pct > 0.9 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="grid grid-cols-[80px_1fr_170px] items-center gap-2 text-xs" title={hint}>
      <span className="text-gray-500">{label}</span>
      <div className="bg-gray-100 rounded h-2">
        <div className={`h-2 rounded ${color}`} style={{ width: `${Math.min(pct, 1) * 100}%` }} />
      </div>
      <span className="text-gray-600 text-right">
        {money(spent)} / {money(budget)}{' '}
        <b className={pct > 1 ? 'text-red-600' : 'text-gray-900'}>{Math.round(pct * 100)}%</b>
      </span>
    </div>
  );
};

// Actual vs budget per project: Material (factory PO purchases), Site (site
// expenses + site-side vendor bills) and Total. rows come from
// /expenses/dashboard/budget-vs-actual - one selected project or the top 20
// by cost.
export const BudgetVsActual = ({ rows, onProjectClick }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-gray-400">No project spend recorded yet.</p>;
  }
  return (
    <div className="space-y-4">
      {rows.map((r) => (
        <div key={r.project_id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
          <div className="mb-2">
            <button
              type="button"
              onClick={onProjectClick ? () => onProjectClick(r) : undefined}
              className="font-medium text-sm text-gray-900 hover:underline text-left"
            >
              {r.project_name}
            </button>
            {r.status && r.status !== 'active' && (
              <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">{r.status.replace('_', ' ')}</span>
            )}
          </div>
          <div className="space-y-1.5">
            {/* Material's budget here is already the reduced 95% figure -
                5% of the raw allotted material budget is carved out as the
                Labour budget below (2026-09-29 user instruction). */}
            <BudgetBar
              label="Material"
              spent={r.material_actual}
              budget={r.budgeted_material_cost}
              hint={r.budgeted_labour_cost != null ? `${money(r.budgeted_labour_cost)} of the original material budget is reserved for labour, below` : undefined}
            />
            <BudgetBar
              label="Site"
              spent={r.site_actual}
              budget={r.budgeted_site_cost}
              hint={`Site expenses ${money(r.site_expenses)} + vendor bills ${money(r.vendor_bills)}`}
            />
            {/* Labour (production-tracker daily reports) - budgeted at 5%
                of the project's raw material budget, per 2026-09-29 user
                instruction; null (no bar, spend only) when no material
                budget is set to derive it from. */}
            <BudgetBar label="Labour" spent={r.labour_actual} budget={r.budgeted_labour_cost} />
            <BudgetBar label="Total" spent={r.total_actual} budget={r.budgeted_total} />
          </div>
        </div>
      ))}
    </div>
  );
};
